import React from 'react';
import styled from 'styled-components';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ChartCard } from './ChartCard';
import { colors } from '../../theme/tokens';
import { DEMO_COUNTRY_DISTRIBUTION } from './demoData';

const DEMO_DATA = DEMO_COUNTRY_DISTRIBUTION;

const TOP_COUNTRIES = DEMO_DATA.slice(0, 5).map((c, i) => ({
  ...c,
  rank: i + 1,
  diff: c['Client Articles'] - c['Concurrent Moyenne']
}));

export const CountryDistributionCard: React.FC = () => (
  <ChartCard
    title="Distribution par pays (2026)"
    subtitle="Top pays - Client vs Concurrent Moyenne"
  >
    <ChartWrap>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={DEMO_DATA}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
          <XAxis
            dataKey="country"
            tick={{ fontSize: 11, fill: colors.inkMuted }}
          />
          <YAxis width={40} tick={{ fontSize: 11, fill: colors.inkMuted }} />
          <Tooltip />
          <Legend />
          <Bar dataKey="Client Articles" fill={colors.indigo} />
          <Bar dataKey="Concurrent Moyenne" fill={colors.inkFaint} />
        </BarChart>
      </ResponsiveContainer>
    </ChartWrap>

    <DetailSection>
      <DetailTitle>Meilleurs pays</DetailTitle>
      <DetailList>
        {TOP_COUNTRIES.map((c) => (
          <DetailItem key={c.country}>
            <DetailLeft>
              <DetailRank>#{c.rank}</DetailRank>
              <DetailName>{c.country}</DetailName>
            </DetailLeft>
            <DetailRight>
              <DetailCount>{c['Client Articles']} articles</DetailCount>
              <DetailDiff $positive={c.diff >= 0}>
                {c.diff >= 0 ? '+' : ''}
                {((c.diff / (c['Concurrent Moyenne'] || 1)) * 100).toFixed(1)}%
                vs avg
              </DetailDiff>
            </DetailRight>
          </DetailItem>
        ))}
      </DetailList>
    </DetailSection>
  </ChartCard>
);

// --- Styled ---

const ChartWrap = styled.div`
  margin-top: 8px;
`;

const DetailSection = styled.div`
  margin-top: 20px;
`;

const DetailTitle = styled.p`
  font-family: var(--font-sans-semibold, var(--font-sans));
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  margin: 0 0 10px;
`;

const DetailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
`;

const DetailLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DetailRank = styled.span`
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-faint);
`;

const DetailName = styled.span`
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
`;

const DetailRight = styled.div`
  text-align: right;
`;

const DetailCount = styled.p`
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--ink-muted);
  margin: 0;
`;

const DetailDiff = styled.p<{ $positive: boolean }>`
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => (p.$positive ? colors.olive : colors.crimson)};
  margin: 2px 0 0;
`;
